import { X as XIcon } from '@ui/components/icons';
import { isProdEnv } from '@ui/constants/env';
import { Portal } from '@ui/lib/park/portal';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Spinner } from '@ui/lib/park/ui/spinner';
import type {
  config,
  WormholeConnectTheme,
} from '@wormhole-foundation/wormhole-connect';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { Center, Flex, styled } from 'styled-system/jsx';

import { useWormholeDialogStore } from './useWormholeDialogStore';

const WormholeConnect = dynamic(
  () => import('@wormhole-foundation/wormhole-connect'),
  {
    ssr: false,
    loading: () => (
      <Center h="600px">
        <Spinner size="default" />
      </Center>
    ),
  }
);

// Mainnet/Testnet configs
const mainnetConfig: config.WormholeConnectConfig = {
  network: 'Mainnet',
  chains: [
    'Solana',
    'Monad',
    'Ethereum',
    'Sui',
    'Base',
    'Bsc',
    'Arbitrum',
    'Avalanche',
    'HyperEVM',
    'Polygon',
    'Xlayer',
    'Linea',
    'Berachain',
  ],
  ui: {
    title: 'Wormhole Bridge',
    defaultInputs: {
      source: { chain: 'Arbitrum', token: 'USDC' },
      destination: { chain: 'Monad', token: 'USDC' },
      requiredChain: 'Monad',
    },
  },
};

const testnetConfig: config.WormholeConnectConfig = {
  network: 'Testnet',
  // Limit available chains on testnet; destination will be fixed to Monad via defaultInputs below
  chains: [
    'Solana',
    'Monad',
    'Ethereum',
    'Sui',
    'Base',
    'Bsc',
    'Arbitrum',
    'Avalanche',
    'HyperEVM',
    'Polygon',
    'Xlayer',
    'Linea',
    'Berachain',
  ],
  rpcs: {
    Monad: 'https://monad-testnet.g.alchemy.com/v2/I67C-15XPZgdh7y03ztR5',
  },
  // validateTransferHandler: async (transferDetails) => {
  //   const { fromChain, toChain } = transferDetails;
  //   if (toChain !== 'Monad') {
  //     return { isValid: false, error: 'Destination must be Monad testnet' };
  //   }
  //   if (fromChain === 'Monad') {
  //     return {
  //       isValid: false,
  //       error: 'Source chain cannot be Monad on testnet',
  //     };
  //   }
  //   return { isValid: true };
  // },
  ui: {
    title: 'Wormhole Bridge',
    defaultInputs: {
      source: { chain: 'Arbitrum', token: 'USDC' },
      destination: { chain: 'Monad', token: 'USDC' },
      requiredChain: 'Monad',
    },
  },
};

const wormholeConfig: config.WormholeConnectConfig = isProdEnv
  ? mainnetConfig
  : testnetConfig;

const theme: WormholeConnectTheme = {
  // Define the theme
  mode: 'dark',
  primary: '#CCFF00',
  // background: '#131314',
  input: '#1f1f20',
};

export const WormholeWrapper = () => {
  const { t } = useTranslation();
  const isOpen = useWormholeDialogStore((s) => s.isOpen);
  const setOpen = useWormholeDialogStore((s) => s.setOpen);

  // Preload the component when this wrapper is mounted (e.g., on the Trade page)
  // This ensures the chunks start downloading before the user clicks open
  // Since we use ssr: false, this effect only runs on the client
  if (typeof window !== 'undefined') {
    // Trigger the dynamic import to start preloading
    // @ts-ignore
    import('@wormhole-foundation/wormhole-connect');
  }

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      closeOnInteractOutside={false}
      open={isOpen}
      onOpenChange={(e) => setOpen(e.open)}
    >
      {/* <Dialog.Trigger asChild></Dialog.Trigger> */}
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="v2.bg.panel"
            color="v2.text.primary"
            border="1px solid"
            borderColor="v2.border.default"
            borderRadius="4px"
            boxShadow="modal"
            w={{ base: '92vw', tablet: '520px', desktop: '520px' }}
            maxW="95vw"
            p={0}
          >
            <Dialog.Title asChild p={6} pb={0} mb="-15px">
              <Flex justifyContent="space-between" alignItems="center">
                <styled.span />
                {/* <styled.span>{t('common.Wormhole Bridge')}</styled.span> */}
                <Dialog.CloseTrigger
                  boxShadow="none"
                  bg="none"
                  aria-label="Close"
                >
                  <Icon w="20px" h="20px">
                    <XIcon />
                  </Icon>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Title>
            <Dialog.Description p={0}>
              {isOpen && (
                <WormholeConnect config={wormholeConfig} theme={theme} />
              )}
            </Dialog.Description>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
